import { useCallback, useEffect, useRef, useState } from 'react'
import { type AiChatFileAttachmentConfig, F0Dialog, type UploadedFile } from '@factorialco/f0-react'
import { Tabs } from '@factorialco/f0-react/dist/experimental'
import i18n from 'modules/core/lib/i18n'
import { useTicketingPolicies } from 'modules/ticketing/hooks/useTicketingPolicies'
import { trackOpenCreateTicketDialog } from 'modules/ticketing/lib/tracking'

import { CreateWithAiTab } from './CreateWithAiTab'
import { ManualFormTab, type ManualFormInitialValues } from './ManualFormTab'

interface CreateTicketDialogProps {
  isOpen: boolean
  onClose: () => void
  initialValues?: ManualFormInitialValues
  // Attachment pipeline owned by the AI chat integration (the activity); the
  // dialog just forwards it to the AI tab's composer.
  fileAttachments?: AiChatFileAttachmentConfig
  onLaunchAiTicketCreation: (
    message: string,
    options?: { source?: 'description' | 'category'; files?: UploadedFile[] }
  ) => void
}

export interface CreateTicketTabHandle {
  submit: () => void
}

export interface CreateTicketTabSubmitState {
  disabled: boolean
  loading?: boolean
}

const hasInitialValues = (initialValues?: ManualFormInitialValues) =>
  Boolean(
    initialValues?.title ||
    initialValues?.description ||
    initialValues?.categoryId ||
    initialValues?.categoryName ||
    initialValues?.priority
  )

const CREATE_WITH_AI_TAB = 'create-with-ai'
const USE_FORM_TAB = 'use-form'
const DEFAULT_SUBMIT_STATE = { disabled: true, loading: false }

export const CreateTicketDialog = ({
  isOpen,
  onClose,
  initialValues,
  fileAttachments,
  onLaunchAiTicketCreation,
}: CreateTicketDialogProps) => {
  const { aiEnabled } = useTicketingPolicies()
  const canUseAiTab = aiEnabled === true
  const shouldOpenManualForm = hasInitialValues(initialValues)
  const formTabRef = useRef<CreateTicketTabHandle>(null)
  const wasOpenRef = useRef(false)
  const [activeTab, setActiveTab] = useState(
    canUseAiTab && !shouldOpenManualForm ? CREATE_WITH_AI_TAB : USE_FORM_TAB
  )
  const [formSubmitState, setFormSubmitState] =
    useState<CreateTicketTabSubmitState>(DEFAULT_SUBMIT_STATE)

  const isAiTabActive = canUseAiTab && activeTab === CREATE_WITH_AI_TAB
  const handleFormSubmit = useCallback(() => {
    formTabRef.current?.submit()
  }, [])

  // The AI tab submits through F0AiChatTextArea's own send button, so the
  // dialog only needs a footer primary action for the manual form tab.
  const primaryAction = isAiTabActive
    ? undefined
    : {
        label: i18n.t('ticketing.inbox.create.create_button'),
        onClick: handleFormSubmit,
        disabled: formSubmitState.disabled,
        loading: formSubmitState.loading,
      }

  useEffect(() => {
    const opened = isOpen && !wasOpenRef.current
    wasOpenRef.current = isOpen

    if (opened) {
      trackOpenCreateTicketDialog()
      setActiveTab(canUseAiTab && !shouldOpenManualForm ? CREATE_WITH_AI_TAB : USE_FORM_TAB)
    }
  }, [canUseAiTab, isOpen, shouldOpenManualForm])

  return (
    <F0Dialog
      isOpen={isOpen}
      module={{ id: 'inbox', label: i18n.t('ticketing.inbox.create.new_ticket') }}
      onClose={onClose}
      primaryAction={primaryAction}
      secondaryAction={{
        label: i18n.t('common.cancel'),
        onClick: onClose,
      }}
    >
      {canUseAiTab && (
        <Tabs
          tabs={[
            { id: CREATE_WITH_AI_TAB, label: i18n.t('ticketing.inbox.create.ai_tab') },
            { id: USE_FORM_TAB, label: i18n.t('ticketing.inbox.create.form_tab') },
          ]}
          activeTabId={activeTab}
          setActiveTabId={setActiveTab}
        />
      )}

      {isAiTabActive ? (
        <CreateWithAiTab
          active={isAiTabActive}
          isOpen={isOpen}
          fileAttachments={fileAttachments}
          onLaunchAiTicketCreation={onLaunchAiTicketCreation}
        />
      ) : (
        <ManualFormTab
          ref={formTabRef}
          isOpen={isOpen}
          initialValues={initialValues}
          onClose={onClose}
          onSubmitStateChange={setFormSubmitState}
        />
      )}
    </F0Dialog>
  )
}
